import React, { useState } from 'react'
import { CouponSeparator } from 'app-v2/assets/images/CouponSeparator'
import { Button } from 'app-v2/design-systems/components/inputs/button/Button'
import { BlazonBackground } from 'app-v2/assets/icons/BlazonBackground'
import { useIntl } from 'react-intl'
import {
    couponCategoryCode,
    getStatusProps,
    isDefined,
    restaurantEligibilityScope
} from 'shared/utils'
import { ZoomModal } from 'app/common/coupon/zoom-modal'
import { COLOR } from 'app-v2/design-tokens'
import type { Coupon as CouponType } from 'shared/types'
import {
    StyledBlazonContainer,
    StyledButtonContainer,
    StyledContainer,
    StyledCouponBottomContainer,
    StyledCouponContainer,
    StyledCouponExpirationDate,
    StyledCouponLocation,
    StyledCouponSeparatorContainer,
    StyledCouponSubInfoContainer,
    StyledCouponTitle,
    StyledCouponTopContainer,
    StyledEnableMessage,
    StyledImage,
    StyledImageContainer
} from './styles'
import { colors } from 'app-v2/design-systems'

const RestaurantEligibility = ({ eligibility }) => {
    const intl = useIntl()
    let message

    switch (eligibility.scope) {
        case restaurantEligibilityScope.ALL: {
            message = intl.formatMessage({
                id: 'kingdom.privileges.coupon.eligibility.all'
            })

            break
        }
        case restaurantEligibilityScope.MULTIPLE: {
            message = intl.formatMessage(
                { id: 'kingdom.privileges.coupon.eligibility.multiple' },
                { restaurantNumber: eligibility.number }
            )

            break
        }
        case restaurantEligibilityScope.ONE: {
            message = eligibility.name

            break
        }
    }
    return <StyledCouponLocation>{message}</StyledCouponLocation>
}

type CouponProps = {
    description: string,
    imageUrl: Array<string>,
    backgroundImageUrl?: string,
    label: string,
    status: 'BURNED' | 'EXPIRED' | 'VALID',
    category: 'KINGDOM' | 'NOW',
    endDate: string,
    restaurants: Array<string>,
    fromEcommerce?: boolean,
    isSelected?: boolean,
    id: string,
    index?: number,
    selectCoupon?: () => void,
    blazonProfileImageUrl?: string,
    backgroundColor?: string,
    coupon: CouponType
}

export const Coupon = ({
    description,
    imageUrl,
    backgroundImageUrl,
    label,
    status,
    category,
    endDate = '',
    restaurants,
    fromEcommerce = false,
    isSelected,
    id,
    index,
    selectCoupon,
    blazonProfileImageUrl = '',
    backgroundColor,
    coupon
}: CouponProps) => {
    const intl = useIntl()

    const [showZoomModal, setShowZoomModal] = useState(false)
    const topBackgroundColor = backgroundColor || COLOR.PRIMARY
    const kingdom = category === couponCategoryCode.KINGDOM
    const { opacity, enable } = getStatusProps(status)

    const disableMessage = enable
        ? ''
        : intl.formatMessage({
              id: `kingdom.privileges.coupon.stamp.${status}`
          })
    const expirationDate = intl.formatMessage(
        { id: 'kingdom.privileges.coupon.validity' },
        { date: endDate }
    )
    const location = restaurants
        ? restaurants.length > 1
            ? intl.formatMessage({
                  id: 'kingdom.privileges.coupon.restaurant.label'
              })
            : restaurants[0]
        : ''
    const ctaLabel = intl.formatMessage({
        id: !!selectCoupon
            ? 'kingdom.privilege.select.coupon'
            : 'kingdom.privilege.see.coupon'
    })
    const hasId = isDefined(id) && id !== ''

    const toggleModal = () => {
        setShowZoomModal(!showZoomModal)
    }

    return (
        <StyledContainer index={index}>
            {!enable && !!disableMessage && (
                <StyledEnableMessage
                    color={kingdom ? colors.fieryRed : colors.fieryRed}>
                    {disableMessage}
                </StyledEnableMessage>
            )}
            <StyledCouponContainer
                opacity={opacity}
                fromEcommerce={fromEcommerce}>
                {/* TODO: check MR (!2510) for linear gradient background */}
                <StyledCouponTopContainer
                    backgroundImageUrl={backgroundImageUrl}
                    topBackgroundColor={topBackgroundColor}>
                    <StyledImageContainer>
                        <StyledImage src={imageUrl} />
                    </StyledImageContainer>
                    {blazonProfileImageUrl && (
                        <StyledBlazonContainer>
                            <BlazonBackground />
                        </StyledBlazonContainer>
                    )}
                </StyledCouponTopContainer>
                <StyledCouponSeparatorContainer>
                    <CouponSeparator />
                </StyledCouponSeparatorContainer>
                <StyledCouponBottomContainer>
                    <StyledCouponTitle>{label}</StyledCouponTitle>
                    <StyledCouponSubInfoContainer>
                        {endDate && (
                            <StyledCouponExpirationDate>
                                {expirationDate}
                            </StyledCouponExpirationDate>
                        )}
                        {!!coupon.restaurantEligibility && !location && (
                            <RestaurantEligibility
                                eligibility={coupon.restaurantEligibility}
                            />
                        )}

                        {location && (
                            <StyledCouponLocation>
                                {location}
                            </StyledCouponLocation>
                        )}
                    </StyledCouponSubInfoContainer>
                </StyledCouponBottomContainer>
            </StyledCouponContainer>
            {enable && (
                <StyledButtonContainer>
                    <Button
                        onClick={() => {
                            if (selectCoupon) {
                                selectCoupon()
                            } else if (hasId) {
                                toggleModal()
                            }
                        }}
                        buttonColor={colors.darkMayo}
                        variant={'contained'}
                        children={ctaLabel}
                        labelColor={colors.bbqBrown}
                        small
                    />
                </StyledButtonContainer>
            )}
            {showZoomModal && (
                <ZoomModal
                    isOpen={showZoomModal}
                    endDate={endDate}
                    id={id}
                    title={label}
                    legalMentions={description}
                    restaurants={restaurants}
                    handleClose={toggleModal}
                    className="coupons-modal"
                    blazonProfileImageUrl={blazonProfileImageUrl}
                    imageUrl={imageUrl[0]}
                />
            )}
        </StyledContainer>
    )
}
